import React from 'react';
import { CatalogItem, CATALOG_STATUS_MAP } from '../models';
import { downloadFileWithFallback } from 'helpers/downloadFile';
import {
    RiHistoryLine,
    RiDownload2Line,
    RiCheckDoubleLine,
    RiTimeLine,
} from 'react-icons/ri';

interface CatalogVersionHistoryProps {
    versions: CatalogItem[];
    onDownloadVersion?: (version: CatalogItem) => void;
}

const CatalogVersionHistory: React.FC<CatalogVersionHistoryProps> = ({
    versions = [],
    onDownloadVersion,
}) => {
    if (!versions || versions.length === 0) {
        return (
            <div className="p-8 text-center bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-100 dark:border-gray-700 my-4">
                <RiHistoryLine className="mx-auto text-gray-400 text-3xl mb-2" />
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    No version history available for this document.
                </p>
            </div>
        );
    }

    return (
        <div className="py-4">
            <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-6 flex items-center">
                <RiHistoryLine className="mr-2 text-[#4c8b6a]" size={22} />
                Version History Timeline
            </h3>

            <div className="relative border-l-2 border-[#4c8b6a]/20 ml-4 pl-6 space-y-8">
                {versions.map((ver, idx) => {
                    const isLatest = idx === 0;
                    const isPublished = ver.status === 1;
                    const uploaderName =
                        ver.createby_id_name ||
                        ver.createdby_name ||
                        'System User';
                    const initials = uploaderName
                        .split(' ')
                        .map((n) => n[0])
                        .join('')
                        .substring(0, 2)
                        .toUpperCase();

                    return (
                        <div key={ver.id || idx} className="relative group">
                            {/* Timeline node dot */}
                            <div
                                className={`absolute -left-[33px] top-1.5 w-4 h-4 rounded-full border-2 transition-transform group-hover:scale-125 ${
                                    isLatest
                                        ? 'bg-[#4c8b6a] border-white dark:border-gray-800 shadow-md ring-4 ring-[#4c8b6a]/20'
                                        : 'bg-white dark:bg-gray-700 border-[#4c8b6a]/40'
                                }`}
                            />

                            {/* Card content */}
                            <div
                                className={`p-5 rounded-2xl border transition-all duration-300 ${
                                    isLatest
                                        ? 'bg-gradient-to-br from-[#4c8b6a]/5 to-white dark:from-[#4c8b6a]/10 dark:to-gray-800 border-[#4c8b6a]/30 shadow-sm'
                                        : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700/80 hover:border-gray-200 dark:hover:border-gray-600'
                                }`}
                            >
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                                    <div className="flex items-center space-x-2.5">
                                        <span className="px-3 py-1 rounded-lg bg-[#4c8b6a] text-white font-extrabold text-xs shadow-sm">
                                            {ver.version ||
                                                `V1.${versions.length - idx}`}
                                        </span>
                                        {isLatest && (
                                            <span className="px-2 py-0.5 rounded-md bg-[#4c8b6a]/15 text-[#4c8b6a] font-bold text-[10px] uppercase tracking-wider">
                                                Current Version
                                            </span>
                                        )}
                                        <span
                                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
                                                isPublished
                                                    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400'
                                                    : 'bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400'
                                            }`}
                                        >
                                            {isPublished ? (
                                                <RiCheckDoubleLine className="mr-1" />
                                            ) : (
                                                <RiTimeLine className="mr-1" />
                                            )}
                                            {CATALOG_STATUS_MAP[ver.status] ||
                                                'Unknown'}
                                        </span>
                                    </div>

                                    <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">
                                        {new Date(
                                            ver.ts_created || Date.now(),
                                        ).toLocaleString('en-GB')}
                                    </span>
                                </div>

                                <p className="text-sm text-gray-700 dark:text-gray-300 mb-4 bg-gray-50 dark:bg-gray-900/50 p-3 rounded-xl border border-gray-100 dark:border-gray-800/80 italic">
                                    &ldquo;
                                    {ver.description ||
                                        'No description provided'}
                                    &rdquo;
                                </p>

                                <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700/60">
                                    <div className="flex items-center">
                                        <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-[#4c8b6a] to-[#3d7055] text-white font-bold text-[10px] flex items-center justify-center mr-2 shadow-sm">
                                            {initials}
                                        </div>
                                        <div className="text-xs">
                                            <span className="font-semibold text-gray-800 dark:text-gray-200">
                                                {uploaderName}
                                            </span>
                                            {/* <span className="text-gray-400 ml-1.5 text-[11px]">
                                                (Author)
                                            </span> */}
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => {
                                            if (onDownloadVersion)
                                                onDownloadVersion(ver);
                                            else if (ver.file) {
                                                const dlName = ver.originalFileName || ver.file.split('/').pop() || 'document';
                                                downloadFileWithFallback(ver.file, dlName);
                                            }
                                        }}
                                        className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold text-[#4c8b6a] hover:text-white hover:bg-[#4c8b6a] border border-[#4c8b6a]/30 transition-all duration-200 shadow-sm"
                                    >
                                        <RiDownload2Line
                                            className="mr-1.5"
                                            size={14}
                                        />
                                        Download {ver.version}
                                        {/* (
                                        {ver.file?.split('/').pop() ||
                                            'document'}
                                        ) */}
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default CatalogVersionHistory;
